<template>
	<layout-admin>
		<ui-modal
			ref="confirmModal"
			:small="true"
			confirm-label="Stornieren"
			v-on:confirm="
				$inertia.post(
					$route('admin.offers_buy_price_indication.cancel', {
						id: offer.id,
					})
				)
			"
			v-on:dismiss="showCancelPriceIndicationsModal = false"
			v-on:close="showCancelPriceIndicationsModal = false"
			v-if="showCancelPriceIndicationsModal"
			>Wollen Sie die Preisindikationen wirklich stornieren?
		</ui-modal>

		<div class="p-6">
			<h1 class="text-4xl font-semibold text-gray-900 mb-6">
				Flächengebot
				{{
					$cropOptions.getLocalizedLabel(
						offer.priceindication.status,
						$cropOptions.priceIndicationStatusOptions
					)
				}}
			</h1>

			<div class="grid grid-cols-2 gap-x-6">
				<div class="bg-white rounded-xl shadow-lg divide-y divide-gray-200">
					<ui-alert-errors
						v-if="otherErrors.length > 0"
						:messages="otherErrors"
					/>

					<ui-input-group
						class="p-6"
						v-model="form.priceindication.distance_max"
						field="priceindication.distance_max"
						label="Maximum Distance"
						suffix="km"
						:form="form"
						:required="true"
						:dp="0"
					>
					</ui-input-group>

					<offer-edit-price
						class="p-6"
						:form="form"
						:activeFutures="activeFutures"
						:grid="{
							cols: '4',
							flat: { colSpan: [4] },
							indexed: { colSpan: [2, 1] },
						}"
						:is-price-required="true"
					/>

					<div class="p-6">
						<!-- Frachtkosten … -->
						Frachtkosten werden berechnet relativ zu
						{{ $cropOptions.locationToString(offer) }}

						<!-- Price calculated -->
						<div v-if="offer.price.type == 'INDEXED'" class="font-semibold">
							<span v-if="offer.price.commodity_future == ''">
								Kein Future ausgewählt!
							</span>
							<span v-else>
								Aktueller Preis ca.
								{{ currentPriceEstimate }}
							</span>
						</div>
					</div>

					<!-- Statistics? -->
					<div class="p-6" v-if="offer.priceindication__status == 'ACTIVE'">
						<h2 class="text-2xl font-semibold text-gray-900 mb-4">Statistik</h2>
						Erreichte Landwirte: {{ offer.priceIndications.length }}<br />
						Davon mit Bestpreis:
						{{ offer.priceIndications.filter((o) => o._is_best_price).length
						}}<br />
					</div>

					<!-- CTA -->
					<div class="p-6 space-x-4">
						<ui-button
							label="Flächengebot aktualisieren"
							v-on:click="submitForm()"
							:disabled="submitButtonDisabled"
							:type="submitButtonDisabled ? 'secondary' : 'primary'"
						/>
						<ui-button
							label="Stornieren"
							v-on:click="showCancelPriceIndicationsModal = true"
							v-if="offer.priceindication.status == 'ACTIVE'"
						/>
					</div>
				</div>

				<div class="pl-12 pt-6">
					<!-- tradeable terms -->
					<tradable-terms-summary
						:offer="offer"
						:commodity-future="activeFuture"
					/>

					<div v-if="offer.priceindication.status == 'ACTIVE'">
						<h2 class="text-2xl font-semibold text-gray-900 mb-4">Statistik</h2>
						Erreichte Landwirte: {{ offer.priceIndications.length }}<br />
						Davon mit Bestpreis:
						{{ offer.priceIndications.filter((o) => o._is_best_price).length
						}}<br />
					</div>
				</div>
			</div>
		</div>

		<admin-crop-grid
			class="mt-6"
			title="Aktuelle Flächengebote"
			:rows="offer.priceIndications"
			:cols="priceIndicationsGridCols"
			v-bind:row-on-click-handler="rowOnClickHandler"
		/>
	</layout-admin>
</template>

<script>
export default {
	props: {
		offer: {
			type: Object,
			required: true,
		},
		activeFutures: {
			type: Array,
			required: true,
		},
		success: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			form: Vue.observable(this.$inertia.form(this.offer)),
			submitButtonDisabled: false,
			otherErrors: [],
			showCancelPriceIndicationsModal: false,
			priceIndicationsGridCols: [
				{
					id: "id",
					name: "ID",
					render: (id) => "#" + id,
				},
				{
					id: "user__address__zip",
					name: "PLZ",
					render: (id, row) => row.user.address__zip,
				},
				{
					id: "user__address__city",
					name: "Ort",
					render: (id, row) => row.user.address__city,
				},
				{
					id: "user__company_name",
					name: "Betrieb",
					render: (id, row) => row.user.company_name,
				},
				{
					id: "price__freightcost",
					name: "Frachtkosten",
					render: (v) => v + " €",
				},
				{
					id: "price__mt",
					name: "FF-Preis",
					render: (v) => v + " €",
				},
				{
					id: "_is_best_price",
					name: "Bestpreis",
					render: (v) => (v ? this.__("Yes") : this.__("No")),
					filterOptions: [
						{ id: this.__("Yes"), name: this.__("Yes") },
						{ id: this.__("No"), name: this.__("No") },
					],
				},
				{
					id: "updated_at",
					name: this.__("Updated"),
					render: (x) => this.$cropOptions.formatIsoStamp(x),
				},
				{
					id: "notification_sent_at",
					name: this.__("Notification sent"),
					render: (x) => this.$cropOptions.formatIsoStamp(x),
				},
			],
		};
	},
	computed: {
		activeFuture() {
			return this.activeFutures.filter(
				(a) => a.id == this.offer.price.commodity_future_id
			)[0];
		},
		priceIndicationStatus() {
			return this.offer.priceindication;
		},
		currentPriceEstimate() {
			if (this.form.price.type == "INDEXED") {
				if (!this.activeFuture) {
					return null;
				} else {
					console.log(this.activeFuture, this.form.price.basis);
					return (
						(this.activeFuture.euronext_price * 100 +
							this.form.price.basis * 100) /
						100
					).toFixed(2);
				}
			} else {
				return this.form.price.mt; //.toFixed(2);
			}
		},
	},
	methods: {
		async submitForm() {
			let //endpoint = this.$page.url,
				endpoint = this.$route("admin.offers_buy_price_indication", [
					this.offer.id,
				]),
				method = "patch",
				that = this;

			this.submitButtonDisabled = true;
			this.form.submit(method, endpoint, {
				preserveState: true,
				onSuccess(p) {
					console.log("SUCCESS SUBMISSION, got: ", p);
					// that.watchUpdate();
				},
				onFinish(e) {
					that.submitButtonDisabled = false;
					that.updateOtherErrors();
					console.log("submit FINISH", method, endpoint, "got:", e);
				},
			});
		},
		async updateOtherErrors() {
			// save those errors which are not displayed in context in otherErrors
			let otherErrors = JSON.parse(JSON.stringify(this.form.errors));
			this.$bus.$once("is-mark-invalid", function (field, markInvalid) {
				if (markInvalid) {
					delete otherErrors[field];
				}
			});
			this.$bus.$emit("poll-is-mark-invalid");
			this.$nextTick(function () {
				this.otherErrors = Object.values(otherErrors);
			});
		},
		watchUpdate() {
			let that = this;
			this.$inertia.visit(
				this.$route("admin.watch_price_indication", [this.offer.id]),
				{
					data: { updated_at: that.form.updated_at },
					method: "get",
					preserveState: true,
					preserveScroll: true,
					onFinish() {
						// after the long poll finishes, run again the watchUpdate method
						//setTimeout(that.watchUpdate(), 1000);
					},
				}
			);
		},
		rowOnClickHandler(row) {
			console.log("CALLED", row.id.replace("#", ""));
			this.$bus.$emit(
				"toggle-iframe-modal",
				this.$route("admin.email_price_indication", {
					id: row.id.replace("#", ""),
					iframe_modal: 1,
				})
			);
			return null;
		},
	},
	watch: {
		priceIndicationStatus: {
			deep: true,
			handler: function (val) {
				if (val.status == "PENDING" || val.status == "INPROGRESS") {
					//this.watchUpdate();
					setTimeout(this.watchUpdate(), 1000);
					this.submitButtonDisabled = true;
				}
				if (val.status == "ACTIVE" && this.$page.url.includes("watch")) {
					this.submitButtonDisabled = false;
					this.$inertia.visit(
						this.$route("admin.offers_buy_price_indication", [this.offer.id]),
						{
							method: "get",
							preserveState: true,
							preserveScroll: true,
							onFinish() {},
						}
					);
				}
			},
		},
	},
};
</script>
